import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, ArrowRight, FileText, Loader2, RefreshCw } from 'lucide-react';
import type { WorkflowJob } from '../../graphql/types';
import type { TimelineDocument } from '../../titleSearch/timeline';
import { propertyTaxEmptyState } from '../../services/titleSearchFindingsService';
import { EmptyState } from '../../components/ui';
import { loadRecordedDocuments } from './findingsData';
import {
  TAG_STYLES,
  UNTAGGED_DOT,
  PLACEHOLDER,
  buildFindingsRows,
  buildTaxFacts,
  formatInstrumentCount,
  subjectLoanOf,
  taxingUnitsOf,
  type FindingsRow,
} from './findingsModel';

/**
 * FindingsMode (Title Search): the recorded documents oldest first with a type
 * tag, the property tax card, and an ink call-to-action card that opens Title
 * analysis and states the number of instruments.
 *
 * Ordering comes from `titleSearch/timeline.ts` (recorded date, judgment date
 * for judgments); formatting and the placeholder from
 * `titleSearchFindingsService`. Light theme only.
 *
 * Requirements: 10.1, 10.2, 10.3.
 */
export interface FindingsModeProps {
  job: WorkflowJob;
  /** Opens Title analysis (`mode=analysis`). */
  onOpenAnalysis: () => void;
  /** Loads the recorded documents. Defaults to the real service. */
  loadDocuments?: (jobId: string) => Promise<TimelineDocument[]>;
}

const CARD = 'rounded-xl border border-gray-200 bg-white';
const LOAD_ERROR =
  'The recorded documents could not be loaded. Check your connection and try again.';

export const FindingsMode: React.FC<FindingsModeProps> = ({
  job,
  onOpenAnalysis,
  loadDocuments = loadRecordedDocuments,
}) => {
  const [documents, setDocuments] = useState<TimelineDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { jobId } = job;
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDocuments(await loadDocuments(jobId));
    } catch (err) {
      console.error('[FindingsMode] Failed to load documents:', err);
      setError(LOAD_ERROR);
    } finally {
      setLoading(false);
    }
  }, [jobId, loadDocuments]);

  useEffect(() => {
    void load();
  }, [load]);

  const subject = useMemo(
    () => subjectLoanOf(job),
    [job.subjectMortgageRecordingReference, job.subjectLoanAmount] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const rows = useMemo(() => buildFindingsRows(documents, subject), [documents, subject]);

  return (
    <div
      data-testid="findings-mode"
      className="grid max-w-[1300px] grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] gap-4"
    >
      <section aria-labelledby="findings-timeline-title" className={`${CARD} overflow-hidden`}>
        <div className="flex items-center justify-between border-b border-gray-100 px-[18px] py-3.5">
          <h2 id="findings-timeline-title" className="text-base font-bold text-gray-900">
            Recorded documents
          </h2>
          <span className="text-xs text-gray-500">Oldest first · by recorded date</span>
        </div>
        <div className="px-[18px] pb-3.5 pt-2">
          <Timeline rows={rows} loading={loading} error={error} onRetry={() => void load()} />
        </div>
      </section>

      <div className="flex flex-col gap-4">
        <PropertyTaxCard job={job} />
        <AnalysisCallToAction
          count={rows.length}
          loading={loading}
          onOpenAnalysis={onOpenAnalysis}
        />
      </div>
    </div>
  );
};

// ── Timeline ────────────────────────────────────────────────────────────────

const Timeline: React.FC<{
  rows: FindingsRow[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}> = ({ rows, loading, error, onRetry }) => {
  if (loading) {
    return (
      <div
        role="status"
        className="flex items-center justify-center gap-3 py-10 text-sm text-gray-600"
      >
        <Loader2 className="h-5 w-5 animate-spin text-[#834DFB]" aria-hidden="true" />
        Loading recorded documents…
      </div>
    );
  }
  if (error) {
    return (
      <div
        role="alert"
        className="my-3 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4"
      >
        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" aria-hidden="true" />
        <div className="flex-1">
          <p className="text-sm text-red-800">{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            Retry
          </button>
        </div>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <EmptyState
        className="my-3"
        icon={<FileText className="h-5 w-5" aria-hidden="true" />}
        title="No recorded documents yet"
        description="No recorded documents are available for this search."
      />
    );
  }
  return (
    <ol data-testid="findings-timeline" className="m-0 list-none p-0">
      {rows.map((row, index) => (
        <TimelineRow
          key={row.documentId}
          row={row}
          first={index === 0}
          last={index === rows.length - 1}
        />
      ))}
    </ol>
  );
};

/**
 * One entry on the timeline: the date on the left, a dot on a vertical line
 * (the line runs from the first dot to the last), and the document as a card.
 */
const TimelineRow: React.FC<{ row: FindingsRow; first: boolean; last: boolean }> = ({
  row,
  first,
  last,
}) => {
  const dot = row.tag ? TAG_STYLES[row.tag].dot : UNTAGGED_DOT;
  // The line starts at the first dot and ends at the last one.
  const lineSpan = `${first ? 'top-[22px]' : 'top-0'} ${last ? 'h-[22px] bottom-auto' : 'bottom-0'}`;
  return (
    <li
      data-testid="findings-row"
      data-document-id={row.documentId}
      className="grid grid-cols-[96px_16px_minmax(0,1fr)] gap-x-3"
    >
      <div className="pt-[14px] text-[13px] font-bold text-gray-700">
        <time>{row.date}</time>
        {row.dateIsJudgment ? (
          <div className="text-[11px] font-normal text-gray-500">judgment date</div>
        ) : null}
      </div>
      <div className="relative flex justify-center" aria-hidden="true">
        {first && last ? null : (
          <span
            data-testid="findings-line"
            className={`absolute left-1/2 w-px -translate-x-1/2 bg-gray-200 ${lineSpan}`}
          />
        )}
        <span className={`relative z-10 mt-4 h-3 w-3 rounded-full ring-4 ring-white ${dot}`} />
      </div>
      <div
        data-testid="findings-card"
        className={`min-w-0 rounded-xl border border-gray-100 bg-gray-50 px-3.5 py-3 ${last ? '' : 'mb-3'}`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-bold text-gray-900">{row.documentType}</span>
          {row.tag ? (
            <span
              data-testid="findings-tag"
              className={`rounded-[5px] px-[7px] py-px text-[11px] font-bold ${TAG_STYLES[row.tag].pill}`}
            >
              {row.tag}
            </span>
          ) : null}
          {row.amount ? (
            <span className="text-[13px] font-bold text-gray-700">{row.amount}</span>
          ) : null}
        </div>
        <div className="mt-0.5 break-words text-[13px] text-gray-600">
          {row.parties ?? PLACEHOLDER}
        </div>
        <div className="text-xs text-gray-500">Instrument {row.instrument ?? PLACEHOLDER}</div>
      </div>
    </li>
  );
};

// ── Right column ────────────────────────────────────────────────────────────

const PropertyTaxCard: React.FC<{ job: WorkflowJob }> = ({ job }) => {
  const facts = buildTaxFacts(job);
  const units = taxingUnitsOf(job.propertyTaxInformation);
  const emptyMessage = propertyTaxEmptyState(job.propertyTaxInformation);
  return (
    <section aria-labelledby="findings-tax-title" className={`${CARD} p-[18px]`}>
      <h2 id="findings-tax-title" className="text-base font-bold text-gray-900">
        Property tax
      </h2>
      {emptyMessage ? <p className="mt-2 text-[13px] text-gray-600">{emptyMessage}</p> : null}
      <dl data-testid="findings-tax-facts" className="mt-3 grid grid-cols-2 gap-3">
        {facts.map((fact) => (
          <div key={fact.label}>
            <dt className="text-[11px] font-bold uppercase tracking-[0.06em] text-gray-500">
              {fact.label}
            </dt>
            <dd
              className={`mt-0.5 break-words text-sm font-bold ${
                fact.missing ? 'text-gray-500' : 'text-gray-900'
              }`}
            >
              {fact.value}
            </dd>
          </div>
        ))}
      </dl>
      <p data-testid="findings-tax-units" className="mt-3 text-[13px] text-gray-600">
        Taxing units: {units.length > 0 ? units.join(', ') : PLACEHOLDER}
      </p>
    </section>
  );
};

const AnalysisCallToAction: React.FC<{
  count: number;
  loading: boolean;
  onOpenAnalysis: () => void;
}> = ({ count, loading, onOpenAnalysis }) => (
  <section
    aria-labelledby="findings-cta-title"
    data-testid="findings-cta"
    className="rounded-xl bg-[#1c1428] p-[18px]"
  >
    <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#b8afd0]">Next</p>
    <h2 id="findings-cta-title" className="mt-1 text-[17px] font-bold text-white">
      {loading
        ? 'Run title analysis on these instruments'
        : `Run title analysis on these ${formatInstrumentCount(count)}`}
    </h2>
    <p className="mt-1 text-[13px] text-[#c8c0dd]">
      Grades the title, derives lien position and lists exceptions with curative actions.
    </p>
    <button
      type="button"
      onClick={onOpenAnalysis}
      className="mt-3.5 inline-flex items-center gap-1.5 rounded-[9px] bg-[#F0E100] px-4 py-2.5 text-sm font-bold text-[#1c1428] hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1c1428]"
    >
      Open title analysis
      <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </button>
  </section>
);

export default FindingsMode;
